import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import config from '../config';

function Footer() {
  const [settings, setSettings] = useState(null);
  const { app } = config;
  const APP_NAME = app.name;

  useEffect(() => {
    // Load settings from localStorage
    try {
      const savedSettings = localStorage.getItem('admin_settings');
      if (savedSettings) {
        setSettings(JSON.parse(savedSettings));
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  }, []);

  const currentYear = new Date().getFullYear();
  const siteName = settings?.site_name || APP_NAME;
  const siteEmail = settings?.site_email || '';
  const sitePhone = settings?.site_phone || '';
  const siteAddress = settings?.site_address || '';

  return (
    <footer className="bg-gradient-to-b from-gray-900 to-gray-800 text-gray-300 mt-auto rounded-t-2xl sm:rounded-t-3xl">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* About Section */}
          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-bold text-white mb-3">
              About {siteName}
            </h3>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Your trusted online shopping destination. We offer quality products with fast delivery and excellent customer service.
            </p>
            {siteAddress && (
              <div className="pt-2">
                <p className="text-xs sm:text-sm text-gray-400 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{siteAddress}</span>
                </p>
              </div>
            )}
          </div>

          {/* Payment Methods Section */}
          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-bold text-white mb-3">
              We Accept Payment From
            </h3>
            <div className="flex flex-wrap gap-3">
              {/* Visa */}
              <div className="bg-white rounded-lg px-4 py-2.5 flex items-center justify-center h-12 shadow-sm">
                <img 
                  src="/visa.png" 
                  alt="Visa" 
                  className="h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              {/* Mastercard */}
              <div className="bg-white rounded-lg px-4 py-2.5 flex items-center justify-center h-12 shadow-sm">
                <img 
                  src="/mastercard.png" 
                  alt="Mastercard" 
                  className="h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              {/* Mobile Money / GHI */}
              <div className="bg-white rounded-lg px-4 py-2.5 flex items-center justify-center h-12 shadow-sm">
                <img 
                  src="/mobile-money.png" 
                  alt="Mobile Money" 
                  className="h-8 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>

          {/* Social Media & Newsletter Section */}
          <div className="space-y-3">
            <h3 className="text-base sm:text-lg font-bold text-white mb-3">
              Follow Us
            </h3>
            <p className="text-sm sm:text-base text-gray-400 mb-3">
              Stay connected with us on social media for the latest updates and offers.
            </p>
            <div className="flex gap-4">
              {/* Facebook */}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-primary transition-colors duration-200 flex items-center justify-center group"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </a>
              {/* X (formerly Twitter) */}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-primary transition-colors duration-200 flex items-center justify-center group"
                aria-label="X"
              >
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              {/* Instagram */}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-primary transition-colors duration-200 flex items-center justify-center group"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
              {/* WhatsApp */}
              <a
                href={sitePhone ? `https://wa.me/${sitePhone.replace(/[^0-9]/g, '')}` : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-700 hover:bg-primary transition-colors duration-200 flex items-center justify-center group"
                aria-label="WhatsApp"
              >
                <svg className="w-5 h-5 text-gray-300 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-6 sm:mt-8 pt-6 border-t border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs sm:text-sm text-gray-400 text-center sm:text-left">
              © {currentYear} {siteName}. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center sm:justify-end gap-4 sm:gap-6 text-xs sm:text-sm">
              <Link
                to="/support"
                className="text-gray-400 hover:text-primary transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                to="/support"
                className="text-gray-400 hover:text-primary transition-colors duration-200"
              >
                Terms of Service
              </Link>
              <span className="text-gray-600">|</span>
              <Link
                to="/support"
                className="text-gray-400 hover:text-primary transition-colors duration-200"
              >
                Returns & Refunds
              </Link>
            </div>
          </div>
          {/* Developer Credit */}
          <div className="mt-4 pt-4 border-t border-gray-700">
            <p className="text-xs sm:text-sm text-gray-400 text-center">
              <span>Developed by </span>
              <span className="text-orange-500 font-semibold">SherSoft Technology</span>
              <span> • </span>
              <a 
                href="tel:+2330548104944" 
                className="text-gray-400 hover:text-primary transition-colors duration-200"
              >
                +233 [0] 548 104 944
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
